import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { supabase } from '../lib/supabaseClient';
import { APPLICATIONS_BUCKET, uploadFile } from './admin/storage';

const useSubmitApplication = () => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const uploadDocument = async (file, folder) => {
    if (!file) return null;
    const url = await uploadFile(file, folder, APPLICATIONS_BUCKET);
    return url;
  };

  const submitApplication = async (formData) => {
    if (!supabase) {
      toast({
        title: t('application.toastFailedTitle'),
        description: t('application.toastNetworkDesc'),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      throw new Error('Supabase is not configured');
    }

    if (!formData.sex || (formData.sex !== 'male' && formData.sex !== 'female')) {
      toast({
        title: t('application.toastInvalidTitle'),
        description: t('application.toastInvalidDesc'),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      throw new Error('Invalid sex');
    }

    setLoading(true);
    try {
      const [cvUrl, coverLetterUrl, otherDocumentsUrl] = await Promise.all([
        uploadDocument(formData.cv, 'cv'),
        uploadDocument(formData.cover_letter, 'cover-letter'),
        uploadDocument(formData.other_documents, 'other-documents'),
      ]);

      const { error } = await supabase.from('applications').insert({
        job_id: formData.job_id || null,
        job_title: typeof formData.job_title === 'string'
          ? { en: formData.job_title, fr: formData.job_title, sw: formData.job_title }
          : formData.job_title || { en: '', fr: '', sw: '' },
        full_name: formData.full_name,
        email: formData.email,
        place: formData.place,
        nationality: formData.nationality,
        sex: formData.sex,
        years_of_experience: formData.years_of_experience,
        starting_date: formData.starting_date || null,
        cv_url: cvUrl,
        cover_letter_url: coverLetterUrl,
        other_documents_url: otherDocumentsUrl,
      });

      if (error) throw error;

      toast({
        title: t('application.toastSuccessTitle'),
        description: t('application.toastSuccessDesc'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      return { cvUrl, coverLetterUrl, otherDocumentsUrl };
    } catch (error) {
      toast({
        title: t('application.toastFailedTitle'),
        description: error.message || t('application.toastNetworkDesc'),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { submitApplication, loading };
};

export default useSubmitApplication;
